import { useState } from 'react';
import { STAGING_API, STAGING_AUTH } from '../../config/env';
import Logo from '../../assets/images/logo.png';
import { useDispatch } from 'react-redux';
import { show } from 'providers/toastSlice';
import axios from 'axios';
import { FormUpload } from 'components';
import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkCircle02Icon, Loading03Icon, FileUploadIcon } from '@hugeicons/core-free-icons';

const baseUrl = STAGING_API;
const authPath = STAGING_AUTH;

const FILE_FIELDS = [
    {
        section: 'Applicant Documents',
        fields: [
            { name: 'ghanaCardFront',   label: 'Ghana Card — Front',                  required: true  },
            { name: 'ghanaCardBack',    label: 'Ghana Card — Back',                   required: true  },
            { name: 'momoStatement',    label: 'Mobile Money Statement (3 months)',    required: true  },
            { name: 'bankStatement',    label: 'Bank Statement (3 months)',            required: false },
            { name: 'payslips',         label: 'Payslips (3 months)',                  required: true  },
            { name: 'employmentLetter', label: 'Employment Letter',                   required: true  },
        ],
    },
    {
        section: 'Witness Documents',
        fields: [
            { name: 'witnessGhanaCardFront', label: 'Ghana Card — Front (Witness)', required: true },
            { name: 'witnessGhanaCardBack',  label: 'Ghana Card — Back (Witness)',  required: true },
            { name: 'witnessPayslips',       label: 'Payslips — 3 months (Witness)',required: true },
        ],
    },
];

function AJAServices() {
    const dispatch = useDispatch();
    const [fullName, setFullName] = useState('');
    const [files, setFiles]       = useState({});
    const [processing, setProcessing] = useState(false);
    const [submitted, setSubmitted]   = useState(false);

    const setFile = (name) => (value) => setFiles(prev => ({ ...prev, [name]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!fullName.trim()) {
            return dispatch(show({ state: true, message: 'Please enter the applicant full name', type: 'error' }));
        }

        // Check required fields
        for (const { fields } of FILE_FIELDS) {
            for (const f of fields) {
                if (f.required && !files[f.name]) {
                    return dispatch(show({ state: true, message: `"${f.label}" is required`, type: 'error' }));
                }
            }
        }

        setProcessing(true);

        const formData = new FormData();
        formData.append('fullName', fullName.trim());
        Object.entries(files).forEach(([key, file]) => {
            if (file) formData.append(key, file);
        });

        try {
            const res = await axios.post(
                `${baseUrl}${authPath}/partner-docs`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (res.data?.err) {
                dispatch(show({ state: true, message: res.data.err, type: 'error' }));
            } else {
                setSubmitted(true);
            }
        } catch (err) {
            const msg = err?.response?.data?.err || 'Submission failed. Please try again.';
            dispatch(show({ state: true, message: msg, type: 'error' }));
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center py-10 px-4">
            {/* Header */}
            <div className="w-full max-w-2xl mb-8 text-center">
                <img src={Logo} alt="CedisPay" className="h-10 mx-auto mb-5" />
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                    AJ &amp; A Services — Document Upload
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Please upload the required documents for your loan application.
                    All files must be under <span className="font-semibold">10 MB</span>.
                </p>
            </div>

            {submitted ? (
                <div className="w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/[.08] shadow-sm p-10 flex flex-col items-center text-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
                        <HugeiconsIcon icon={CheckmarkCircle02Icon} size={32} className="text-emerald-500" />
                    </div>
                    <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">Documents Submitted</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Your documents have been sent to the CedisPay team. We will be in touch shortly.
                        </p>
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="w-full max-w-2xl space-y-6">
                    {/* Full name card */}
                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/[.08] shadow-sm p-6">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">
                            Full Name <span className="text-amber-500">*</span>
                        </p>
                        <input
                            type="text"
                            value={fullName}
                            onChange={e => setFullName(e.target.value)}
                            placeholder="Enter applicant's full name"
                            required
                            className="w-full h-11 px-3.5 rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition"
                        />
                    </div>

                    {/* File sections */}
                    {FILE_FIELDS.map(({ section, fields }) => (
                        <div key={section} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/[.08] shadow-sm p-6 space-y-5">
                            <div className="flex items-center gap-2.5 pb-3 border-b border-gray-100 dark:border-white/[.06]">
                                <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center shrink-0">
                                    <HugeiconsIcon icon={FileUploadIcon} size={14} className="text-blue-600 dark:text-blue-400" />
                                </div>
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{section}</p>
                            </div>

                            <div className="space-y-4">
                                {fields.map(f => (
                                    <FormUpload
                                        key={f.name}
                                        id={f.name}
                                        name={f.name}
                                        label={f.label}
                                        required={f.required}
                                        setValue={setFile(f.name)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}

                    <button
                        type="submit"
                        disabled={processing}
                        className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-[#1e3a6e] hover:bg-[#162d56] disabled:opacity-60 text-white text-sm font-semibold shadow-sm transition-all"
                    >
                        {processing ? (
                            <>
                                <HugeiconsIcon icon={Loading03Icon} size={16} className="animate-spin" />
                                Submitting...
                            </>
                        ) : (
                            'Submit Documents'
                        )}
                    </button>

                    <p className="text-center text-xs text-gray-400 dark:text-gray-600 pb-4">
                        Documents are sent securely to CedisPay.
                    </p>
                </form>
            )}
        </div>
    );
}

export default AJAServices;
